<template>
  <div>
    <!-- Main Modal -->
    <Modal
      name="teamJoinedModal"
      class="team-joined"
      :class="{ 'for-mobile-app': isMobileApp }"
      :active="active"
      :noTransition="true"
      @close.prevent="onClose"
    >
      <!-- Header Section -->
      <div class="team-joined__top">
        <div class="team-joined__top-info">
          <div class="team-joined__top-title">Присоединяйтесь к командам</div>
          <div class="team-joined__top-subtitle">
            Чтобы участвовать в
            <Link @click.prevent="onNominationsLinkClick">командных номинациях</Link>.
            Выберите по одной команде в категориях
          </div>
        </div>
        <div
          class="team-joined__top-image"
          :style="`background-image: url('${teamSpiritRafiki}');`"
        />
      </div>

      <!-- Tabs Section -->
      <div
        v-if="hasTeamGroups && groupsTabs.length > 1"
        class="team-joined__tabs"
        :class="{ 'is-scrollable': isTabsScrollable, 'is-centered': isTabsCentered }"
      >
        <Tabs
          :items="groupsTabs"
          :activeItem="activeGroupIndex"
          @changed="onChangeTypePage"
        />
      </div>

      <!-- Teams Section -->
      <div class="team-joined__teams-block">
        <!-- Search -->
        <TeamSearchBar
          v-if="showTeamsSearch"
          v-model="search"
          :loading="loading"
          :searchLoading="searchLoading"
          @search="onSearch"
          @show-search="onShowSearch"
        />

        <!-- Teams List -->
        <div class="team-joined__teams">
          <TeamCard
            v-for="(item, i) in items"
            :key="`${item.id || i}-${activeGroupIndex}`"
            :name="item.name"
            :image="item.logo || ''"
            :location="item.location"
            :members="item.members"
            :isParticipant="item.participantJoined"
            :joined="item.participantJoined"
            class="teams__card"
            @click.native="onTeamCardClick(item)"
            @join="item.participantJoined ? null : onShowConfirm(item)"
          />
        </div>

        <!-- Empty State -->
        <div v-if="items.length === 0" class="team-joined__teams team-joined__teams_empty">
          Ничего не найдено.
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1 && showTeamsSearch" class="team-joined__pagination">
          <Pagination
            :currentPage="currentPage"
            :totalPages="totalPages"
            @prev-page="onTeamsChangePage(currentPage - 1)"
            @next-page="onTeamsChangePage(currentPage + 1)"
            @go-page="onTeamsChangePage"
          />
        </div>
      </div>

      <!-- Create Team Section -->
      <TeamCreationSection @create-team="onTeamCreation" />
    </Modal>

    <!-- Search Modal -->
    <Modal
      name="team-search"
      class="team-search"
      title="Поиск команды"
      :active="showSearch"
      :noTransition="true"
      @close.prevent="onSearchClose"
    >
      <div class="team-search__container">
        <div class="team-search__field text-left">
          <div class="field__label">Название</div>
          <div class="field__input">
            <Input
              v-model.trim="search"
              placeholder="Как называется команда?"
              id="team-search"
              noBorder
              mobile
              :disabled="loading"
              @keydown="onSearchKeydown"
            />
          </div>
        </div>
        <div class="divider" />
        <div>
          <Button
            variant="primary"
            size="lg"
            text="Найти"
            iconLeft="search"
            @click="onSearch"
            wide
            mobile
            :disabled="loading"
            :loading="searchLoading"
          />
        </div>
      </div>
    </Modal>

    <!-- Team Creation Warning Modal -->
    <TeamCreationWarningModal
      :active="showTeamCreationModal"
      :title="teamCreateModalTitle"
      :info="teamCreateModalInfo"
      :showSubtext="teamCreateModalSubtext"
      :createLink="teamCreateLink"
      :isMobileApp="isMobileApp"
      @close="onTeamCreationClosed"
      @create="onTeamCreationCreate"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import TeamCard from '@sc-component-library/team-card/src/main';
import Input from '@rr-component-library/input/src/main';
import Pagination from '@/components/Pagination/Pagination.vue';
import TeamSearchBar from './TeamSearchBar.vue';
import TeamCreationSection from './TeamCreationSection.vue';
import TeamCreationWarningModal from './TeamCreationWarningModal.vue';

export default {
  name: 'TeamJoinedModal',
  components: {
    Input,
    TeamCard,
    Pagination,
    TeamSearchBar,
    TeamCreationSection,
    TeamCreationWarningModal,
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Teams list state
      items: [],
      currentPage: 1,
      itemsPerPage: 6,
      totalPages: 0,
      search: '',
      showSearch: false,
      loading: false,
      searchLoading: false,
      hasUsedSearch: false, // Флаг использования поиска

      // Tabs state
      isTabsScrollable: false,
      isTabsCentered: false,

      // Groups state
      itemsTeamGroups: [],
      activeGroupIndex: 0,

      // Team creation modal state
      showTeamCreationModal: false,
    };
  },
  computed: {
    ...mapGetters('settings', ['theme', 'themeDone', 'options', 'resources']),
    ...mapGetters('user', ['isMobileApp']),
    ...mapGetters('teams', ['teams', 'teamGroups']),
    ...mapGetters('nominations', ['ratings']),

    teamSpiritRafiki() {
      return this.resources?.teamSpiritRafiki || '';
    },

    showOffset() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },

    hasTeamGroups() {
      const groups = Array.isArray(this.itemsTeamGroups) ? this.itemsTeamGroups : [];
      return groups.length > 0;
    },

    groupsTabs() {
      const groups = Array.isArray(this.itemsTeamGroups) ? this.itemsTeamGroups : [];
      return groups.map((group) => ({
        title: group.name || group.title || group.key || 'Группа',
        enabled: true,
        icon: group.participantJoined ? 'check-bold' : null,
      }));
    },

    showTeamsSearch() {
      // Если поиск уже использовался, показываем его всегда
      if (this.hasUsedSearch) return true;

      // Показываем поиск только если команд больше 6
      if (!this.teams) return false;
      return typeof this.teams.total === 'number' ? this.teams.total > 6 : false;
    },

    teamCreateModalInfo() {
      return this.options ? this.options.teamCreateModalInfo || [] : [];
    },

    teamCreateModalTitle() {
      return this.options ? this.options.teamCreateModalTitle || 'Требования для регистрации новой команды' : '';
    },

    teamCreateModalSubtext() {
      return this.options && this.options.teamCreateModalSubtext === true;
    },

    teamCreateLink() {
      return '/team/create';
    },
  },
  methods: {
    ...mapActions('teams', ['getTeams', 'getTeamsByGroup', 'getTeamById', 'getTeamGroups']),
    ...mapActions('nominations', ['getRatings']),

    // ===== Tab Management =====
    onChangeTypePage(index) {
      this.activeGroupIndex = index;
      this.currentPage = 1;
      this.loading = true;

      // Сбрасываем поиск при смене группы
      this.search = '';
      this.hasUsedSearch = false;

      // Передаем текущий поисковый запрос при смене группы
      this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage })
        .finally(() => {
          this.loading = false;
          this.$nextTick(this.checkTabsOverflow);
        });
    },

    // ===== Search Management =====
    onShowSearch() {
      if (this.$root.isMobile) {
        this.showSearch = true;
        this.$nextTick(() => {
          const searchInput = document.getElementById('team-search');
          if (searchInput) searchInput.focus();
        });
      }
    },

    onSearchKeydown(event) {
      if (event.key === 'Enter') {
        this.onSearch();
      }
    },

    onSearch() {
      this.showSearch = false;
      this.searchLoading = true;
      this.hasUsedSearch = true; // Отмечаем что поиск использовался

      this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage })
        .then(() => {
          this.currentPage = 1;
        })
        .catch(() => {
          // Ошибка поиска обрабатывается автоматически
        })
        .finally(() => {
          this.searchLoading = false;
        });
    },

    onSearchClose() {
      this.showSearch = false;
    },

    // ===== Team Management =====
    getCurrentTeamGroupId() {
      const groups = this.itemsTeamGroups || [];
      const group = groups[this.activeGroupIndex] || groups[0];
      if (!group) return null;

      return group.teamGroupId || group.id || group.groupId || group.key || group.code || null;
    },

    fetchTeamsByCurrentGroup({ offset, count }) {
      const teamGroupId = this.getCurrentTeamGroupId();

      if (teamGroupId) {
        return this.getTeamsByGroup({
          teamGroupId,
          searchString: this.search,
          offset,
          count,
        })
          .then(() => {
            this.updateTeamsData();
          });
      }

      // Fallback if no groups available
      return this.getTeams({ searchString: this.search, offset, count })
        .then(() => {
          this.updateTeamsData();
        });
    },

    updateTeamsData() {
      this.items = this.teams?.values || [];
      this.totalPages = this.teams ? Math.ceil(this.teams.total / this.itemsPerPage) : 0;
    },

    onTeamsChangePage(page) {
      this.currentPage = page;
      this.loading = true;

      this.fetchTeamsByCurrentGroup({
        offset: this.showOffset,
        count: this.itemsPerPage,
      })
        .finally(() => {
          this.loading = false;
        });
    },

    onShowConfirm(item) {
      this.$emit('join', item);
    },

    onTeamCardClick(item) {
      if (item.participantJoined === true) {
        // User is already in team - show team details
        this.$emit('close');
        this.$emit('show-team-info', { team: item, fromModal: true });
      } else {
        // Check if user is in any team in current group
        const hasJoinedTeamInGroup = this.items.some((team) => team.participantJoined === true);

        if (hasJoinedTeamInGroup) {
          // User is in another team in this group - show details without join option
          this.$emit('show-team-details', { team: item, canJoin: false });
        } else {
          // New group - show details with join option
          this.$emit('show-team-details', { team: item, canJoin: true });
        }
      }
    },

    // ===== Team Creation Management =====
    onTeamCreation() {
      this.showTeamCreationModal = true;
      this.$emit('close'); // Закрываем главную модалку
    },

    onTeamCreationClosed() {
      this.showTeamCreationModal = false;
    },

    onTeamCreationCreate() {
      this.onTeamCreationClosed();
      setTimeout(() => {
        this.$root.$goPage('TeamCreate', `${this.teamCreateLink}?title=Команда`);
      }, 250);
    },

    onClose() {
      // Сбрасываем ошибку при закрытии модалки
      this.$emit('close');
    },

    onNominationsLinkClick() {
      this.$emit('close');
      // Важно дать модалке "разблокировать" body-scroll (снять has-modal/overflow),
      // иначе после перехода может пропасть скролл.
      setTimeout(() => {
        if (this.isMobileApp && this.$root.$goPage) {
          this.$root.$goPage('NominationsInProgress', '/rating/in-progress&title=Рейтинг');
        } else {
          this.$router.push({
            name: 'NominationsInProgress',
            query: this.isMobileApp ? { addToHistory: 'true' } : {},
          }).catch(() => {});
        }
      }, 250);
    },

    // ===== Data Loading =====
    preload() {
      this.loading = true;

      this.getTeamGroups({
        searchString: this.search,
        offset: 0,
        count: 100,
      })
        .then(() => {
          this.processTeamGroups();
        })
        .catch(() => {
          this.itemsTeamGroups = [];
          this.loading = false;
          this.$nextTick(this.checkTabsOverflow);
        });
    },

    processTeamGroups() {
      const { teamGroups } = this;
      let groups = [];

      if (teamGroups && Array.isArray(teamGroups.values)) {
        groups = teamGroups.values;
      } else if (Array.isArray(teamGroups)) {
        groups = teamGroups;
      }

      this.itemsTeamGroups = groups;

      if (groups.length > 0) {
        this.fetchTeamsByCurrentGroup({ offset: 0, count: this.itemsPerPage })
          .finally(() => {
            this.loading = false;
            this.$nextTick(this.checkTabsOverflow);
          });
      } else {
        this.getTeams({ searchString: this.search, offset: 0, count: this.itemsPerPage })
          .then(() => {
            this.updateTeamsData();
          })
          .finally(() => {
            this.loading = false;
            this.$nextTick(this.checkTabsOverflow);
          });
      }
    },

    // ===== UI Management =====
    checkTabsOverflow() {
      const content = this.findTabsContent();
      if (!content) return;

      const tabItems = this.findTabItems(content);
      if (tabItems.length === 0) {
        this.retryCheckTabsOverflow(100);
        return;
      }

      if (content.scrollWidth === 0 || content.clientWidth === 0) {
        this.retryCheckTabsOverflow(200);
        return;
      }

      const isOverflowing = content.scrollWidth > content.clientWidth;
      this.isTabsScrollable = isOverflowing;
      this.isTabsCentered = !isOverflowing;
    },

    findTabsContent() {
      return this.$el?.querySelector('.team-joined__tabs .tabs__content')
        || this.$el?.querySelector('.tabs__content')
        || this.$el?.querySelector('[class*="tabs"] [class*="content"]');
    },

    findTabItems(content) {
      return content.querySelectorAll('.tabs__tab, .tabs__item, [class*="tab"]');
    },

    retryCheckTabsOverflow(delay) {
      setTimeout(() => {
        this.checkTabsOverflow();
      }, delay);
    },
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.preload();
      }
    },

    groupsTabs() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.checkTabsOverflow();
        }, 500);
      });
    },

    itemsTeamGroups: {
      handler(newGroups) {
        if (Array.isArray(newGroups) && newGroups.length > 0) {
          if (this.activeGroupIndex >= newGroups.length) {
            this.activeGroupIndex = 0;
          }
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.preload();
    window.addEventListener('resize', this.checkTabsOverflow);

    this.$nextTick(() => {
      setTimeout(() => {
        this.checkTabsOverflow();
      }, 500);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkTabsOverflow);
  },
};
</script>

<style lang="scss" scoped>
  @import "./TeamJoinedModal";

  // Prevent text wrapping in tabs
  ::v-deep .tabs__item {
    white-space: nowrap;
  }
</style>
